import { DocumentType, graphql } from '../gql';
import { useEffect, useState } from 'react';
import { useApiMutation, useApiQuery } from '../query/hooks';
import axios, { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';

const requestImageUploadMutation = graphql(`
	mutation useImageUpload_requestImageUpload {
		requestImageUpload {
			id
			uploadUrl
			formDataFields {
				key
				value
			}
		}
	}
`);

const getImageQuery = graphql(`
	query useImageUpload_getImage($id: UUID!) {
		getImage(by: { id: $id }) {
			status
		}
	}
`);

type RequestImageUploadResult = DocumentType<typeof requestImageUploadMutation>['requestImageUpload'];

type UseImageUploadOptions = Readonly<{
	onSuccess?: (imageId: string) => void;
	onError?: () => void;
}>;

type UseImageUploadResult = Readonly<{
	uploadImage: (file: File) => void;
	isLoading: boolean;
	isSuccess: boolean;
	isError: boolean;
}>;

const useImageUpload = ({ onSuccess, onError }: UseImageUploadOptions): UseImageUploadResult => {
	const [file, setFile] = useState<File | null>(null);

	const {
		reset: resetCreate,
		mutate: create,
		data: createResult,
		isError: isCreateError,
		isPending: isCreatePending,
		isSuccess: isCreateSuccess,
	} = useApiMutation(requestImageUploadMutation, {
		onSuccess: ({ requestImageUpload }) => {
			upload(requestImageUpload);
		},
	});

	const {
		reset: resetUpload,
		mutate: upload,
		isPending: isUploadPending,
		isSuccess: isUploadSuccess,
		isError: isUploadError,
	} = useMutation<void, AxiosError, RequestImageUploadResult>({
		mutationFn: async ({ uploadUrl, formDataFields }) => {
			const formData = new FormData();
			formDataFields.forEach(({ key, value }) => formData.append(key, value));
			formData.append('file', file!);

			await axios({
				method: 'POST',
				url: uploadUrl,
				data: formData,
			});
		},
	});

	const {
		isLoading: isProcessingLoading,
		isSuccess: isProcessingSuccess,
		isError: isProcessingError,
	} = useApiQuery(getImageQuery, {
		enabled: createResult != null && isUploadSuccess,
		variables: {
			id: createResult?.requestImageUpload.id ?? '',
		},
		validateResult: ({ getImage }) => {
			if (getImage?.status !== 'uploaded') {
				throw new Error('Pending image processing.');
			}
		},
		retry: 6,
	});

	const uploadImage = (file: File) => {
		setFile(file);
		resetCreate();
		resetUpload();
		create({});
	};

	const isLoading = isCreatePending || isUploadPending || isProcessingLoading;
	const isSuccess = isCreateSuccess && isUploadSuccess && isProcessingSuccess;
	const isError = isCreateError || isUploadError || isProcessingError;

	useEffect(() => {
		if (createResult != null && isProcessingSuccess) {
			onSuccess?.(createResult.requestImageUpload.id);
		}
		// intentionally missing onSuccess callback in order to avoid its memoization
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createResult, isProcessingSuccess]);

	useEffect(() => {
		if (isError) {
			onError?.();
		}
		// intentionally missing onError callback in order to avoid its memoization
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError]);

	return {
		uploadImage,
		isLoading,
		isSuccess,
		isError,
	};
};

export { useImageUpload };
